import css from 'styled-jsx/css'
import { useCallback, useEffect, useReducer, useState } from 'react'
import classnames from 'classnames'
import { formatPhoneNumber } from '../../../utils/format-phone-number'
import { toCapitalize } from '../../../utils/to-capitalize'
import { FormField, Select } from '../../../components/inputs'
import { Button } from '../../../components/button'
import useValidation from '../../../hooks/use-validation'
import { FormFields } from './enums'
import { initialState, reducer } from './reducers/reducer'
import { mapServerErrors, validate } from './validators'
import { mapStatesOptions, mapStateToDto } from './mappers'

// ----------------------------------------------------------------------

const styles = css`
  .wrapper {
    position: relative;
    width: 100%;
    padding: 3.6rem 5.4rem;
    margin-top: 3.5rem;
    margin-bottom: 3.5rem;
    background: var(--secondary-bg-color);
    box-shadow: 0 3px 6px #00000029;
    border-radius: 10px;
  }

  .caption {
    font-size: 1.4rem;
    padding-bottom: 1.4rem;
  }

  .formFieldItem + .formFieldItem {
    padding: 3rem 0 0;
  }

  .formFieldItem {
    :global(#firstName, #lastName) {
      text-transform: capitalize;
    }
  }

  .label {
    display: none;
    margin: 0 0 1rem;
    color: var(--secondary-text-color);
    font-size: 1.4rem;

    &.visible {
      display: inline-block;
    }

    &.error {
      color: var(--alert-color);
    }
  }

  .actions {
    margin-top: 3.8rem;

    .terms {
      padding: 1.2rem 0 1.4rem;
      line-height: 1.9rem;
      text-align: center;
      font-size: 1.4rem;

      .mail {
        color: var(--primary-color);
      }
    }
  }

  .errorText {
    padding: 2.5rem 0 0;
    color: var(--alert-color);
    line-height: 2.1rem;
    font-size: 1.4rem;
  }

  @media (max-width: 1024px) {
    .wrapper {
      width: 100%;
      padding: 1.6rem 2rem 2rem;
      margin-top: 2rem;
      box-shadow: none;
      border-radius: unset;
    }

    .caption {
      font-size: 1rem;
      padding-bottom: 1rem;
    }

    .formFieldItem + .formFieldItem {
      padding: 1.7rem 0 0;
    }

    .label {
      font-size: 1rem;
    }

    .actions {
      .terms {
        font-size: 1.2rem;
      }
    }
  }
`
// ----------------------------------------------------------------------

const useDealerSignUpViewModel = (states, onSubmit, serverErrors) => {
  const [fields, dispatch] = useReducer(reducer, initialState)
  const [statesOptions, setStatesOptions] = useState([])
  const [mappedServerErrors, setMappedServerErrors] = useState({})
  const [showErrors, { isFormValid, errors }] = useValidation(fields, validate)

  useEffect(() => setMappedServerErrors(mapServerErrors(serverErrors)), [serverErrors])

  useEffect(() => setStatesOptions(mapStatesOptions(states)), [states])

  const clearServerErrors = () => {
    setMappedServerErrors({})
  }

  const checkFieldForErrors = (field) => errors[field] || mappedServerErrors[field]

  const handleFieldChange = ({ target: { name, value } }) => {
    dispatch({
      payload: {
        [name]:
          name === FormFields.FirstName || name === FormFields.LastName
            ? toCapitalize(value)
            : value,
      },
    })
    clearServerErrors()
  }

  const handleSelectChange = useCallback((value, { name }) => {
    dispatch({ payload: { [name]: value } })
    clearServerErrors()
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!isFormValid) showErrors()
    else onSubmit(mapStateToDto(fields))
  }

  return {
    fields,
    statesOptions,
    errors,
    mappedServerErrors,
    checkFieldForErrors,
    handleFieldChange,
    handleSelectChange,
    handleSubmit,
  }
}

// ----------------------------------------------------------------------

export default function DealerSignUpForm({
  states,
  statesFetching,
  isLoading,
  serverErrors,
  handleSubmit: onSubmit,
}) {
  const {
    fields,
    statesOptions,
    errors,
    mappedServerErrors,
    checkFieldForErrors,
    handleFieldChange,
    handleSelectChange,
    handleSubmit,
  } = useDealerSignUpViewModel(states, onSubmit, serverErrors)

  return (
    <form className='wrapper' onSubmit={handleSubmit}>
      <div className='flex alignCenter justifyEnd'>
        <span className='caption'>*Required fields </span>
      </div>
      <div>
        {/*First Name*/}
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.FirstName) },
              { visible: checkFieldForErrors(FormFields.FirstName) },
            )}
            htmlFor='firstName'
          >
            {errors[FormFields.FirstName] || mappedServerErrors[FormFields.FirstName]}
          </label>
          <FormField
            placeholder='First Name*'
            type='text'
            id='firstName'
            name={FormFields.FirstName}
            value={fields[FormFields.FirstName]}
            error={checkFieldForErrors(FormFields.FirstName)}
            onChange={handleFieldChange}
          />
        </div>

        {/*Last Name*/}
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.LastName) },
              { visible: checkFieldForErrors(FormFields.LastName) },
            )}
            htmlFor='lastName'
          >
            {errors[FormFields.LastName] || mappedServerErrors[FormFields.LastName]}
          </label>
          <FormField
            placeholder='Last Name*'
            type='text'
            id='lastName'
            name={FormFields.LastName}
            value={fields[FormFields.LastName]}
            error={checkFieldForErrors(FormFields.LastName)}
            onChange={handleFieldChange}
          />
        </div>

        {/*Email*/}
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.Email) },
              { visible: checkFieldForErrors(FormFields.Email) },
            )}
            htmlFor='email'
          >
            {errors[FormFields.Email] || mappedServerErrors[FormFields.Email]}
          </label>
          <FormField
            placeholder='Email*'
            type='email'
            id='email'
            name={FormFields.Email}
            value={fields[FormFields.Email]}
            error={checkFieldForErrors(FormFields.Email)}
            onChange={handleFieldChange}
          />
        </div>

        {/*Address*/}
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.Address) },
              { visible: checkFieldForErrors(FormFields.Address) },
            )}
            htmlFor={FormFields.Address}
          >
            {errors[FormFields.Address] || mappedServerErrors[FormFields.Address]}
          </label>
          <FormField
            placeholder='Address*'
            type='text'
            id={FormFields.Address}
            name={FormFields.Address}
            value={fields[FormFields.Address]}
            error={checkFieldForErrors(FormFields.Address)}
            onChange={handleFieldChange}
          />
        </div>

        {/*City*/}
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.City) },
              { visible: checkFieldForErrors(FormFields.City) },
            )}
            htmlFor='dealershipCity'
          >
            {errors[FormFields.City] || mappedServerErrors[FormFields.City]}
          </label>
          <FormField
            placeholder='City*'
            type='text'
            id='dealershipCity'
            name={FormFields.City}
            value={fields[FormFields.City]}
            error={checkFieldForErrors(FormFields.City)}
            onChange={handleFieldChange}
          />
        </div>

        {/*State*/}
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.State) },
              { visible: checkFieldForErrors(FormFields.State) },
            )}
            htmlFor='dealershipState'
          >
            {errors[FormFields.State] || mappedServerErrors[FormFields.State]}
          </label>

          <Select
            isLoading={statesFetching}
            aria-label='dealershipState'
            id='dealershipState'
            instanceId='dealershipState'
            name='dealershipState'
            placeholder='State*'
            value={fields[FormFields.State]}
            options={statesOptions}
            onChange={handleSelectChange}
            error={checkFieldForErrors(FormFields.State)}
          />
        </div>

        {/*ZipCode*/}
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.ZipCode) },
              { visible: checkFieldForErrors(FormFields.ZipCode) },
            )}
            htmlFor='dealershipZipCode'
          >
            {errors[FormFields.ZipCode] || mappedServerErrors[FormFields.ZipCode]}
          </label>
          <FormField
            placeholder='Postal Code*'
            type='text'
            id='dealershipZipCode'
            name={FormFields.ZipCode}
            value={fields.dealershipZipCode}
            error={checkFieldForErrors(FormFields.ZipCode)}
            onChange={handleFieldChange}
          />
        </div>

        {/*Phone Number*/}
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.PhoneNumber) },
              { visible: checkFieldForErrors(FormFields.PhoneNumber) },
            )}
            htmlFor='phoneNumber'
          >
            {errors[FormFields.PhoneNumber] || mappedServerErrors[FormFields.PhoneNumber]}
          </label>
          <FormField
            placeholder='Phone Number'
            type='tel'
            id='phoneNumber'
            inputMode='numeric'
            maxLength='12'
            name={FormFields.PhoneNumber}
            value={formatPhoneNumber(fields[FormFields.PhoneNumber])}
            error={checkFieldForErrors(FormFields.PhoneNumber)}
            onChange={handleFieldChange}
          />
        </div>

        {/*Dealership*/}
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: checkFieldForErrors(FormFields.DealershipName) },
              { visible: checkFieldForErrors(FormFields.DealershipName) },
            )}
            htmlFor='dealershipName'
          >
            {errors[FormFields.DealershipName] || mappedServerErrors[FormFields.DealershipName]}
          </label>
          <FormField
            placeholder='Dealership*'
            type='text'
            id='dealershipName'
            name={FormFields.DealershipName}
            value={fields.dealershipName}
            error={checkFieldForErrors(FormFields.DealershipName)}
            onChange={handleFieldChange}
          />
        </div>
      </div>

      <div className='flex flexDirectionColumn alignCenter'>
        {mappedServerErrors.general && <p className='errorText'>{mappedServerErrors.general}</p>}
      </div>

      <div className='flex flexDirectionColumn alignCenter actions'>
        <Button type='submit' className='flatbtn signup' disabled={isLoading}>
          Sign Up
        </Button>
        <p className='terms'>
          Questions?{' '}
          <a className='mail' href='mailto:support@drivrzxchange.com'>
            Contact Support
          </a>
        </p>
      </div>

      {/*styles*/}
      <style jsx>{styles}</style>
    </form>
  )
}
